import React from 'react'
import { Box, Typography, IconButton, alpha, useTheme, useMediaQuery } from '@mui/material'
import { Group, Panel, Separator } from 'react-resizable-panels'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import { useTranslation } from 'react-i18next'
import { isMobile } from '../../../utils/utils'
import SearchHeader from './SearchHeader'
import { SearchCitations } from './SearchCitations'
import { SearchPagination } from './SearchPagination'
import SearchFilterSidebar from './SearchFilterSidebar'
import DocumentViewer from '../../../components/document-viewer/DocumentViewer'
import { setSearchDocument } from '../../../redux/actions/searchAction'
import { useSearchTokens } from '../searchDesignTokens'
import TutorialPinpoint from '../../../components/TutorialPinpoint'

const SearchResultsView = ({
  mode,
  setMode,
  onOpenVideoModal,
  totalResultCount,
  onClearSearch,
  searchDocument,
  isSearchLoadingDocument,
  searchDocumentSourceUrl,
  searchSelectedCitation,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dk = useSearchTokens()
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  const resultsPanel = (
    <Box
      sx={{
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'space-between',
          px: 1,
          pt: 1,
          pb: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="h6"
          component="h1"
          sx={{
            fontWeight: 300,
            display: { xs: 'none', md: 'block' },
            fontSize: '1rem',
            color: dk.text1,
            fontFamily: '"Cormorant Garamond", Georgia, "Times New Roman", serif',
            letterSpacing: '-0.01em',
          }}
        >
          {t('legal_search_engine')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TutorialPinpoint storageKey="tutorial_pinpoint_search" placement="left">
            <IconButton
              onClick={onOpenVideoModal}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.18) },
              }}
              title={t('tutorial_video')}
            >
              <PlayCircleOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </TutorialPinpoint>
        </Box>
      </Box>

      {/* Search input */}
      <Box sx={{ px: 1, flexShrink: 0 }}>
        <SearchHeader mode={mode} setMode={setMode} onClearSearch={onClearSearch} totalResultCount={totalResultCount} />
      </Box>

      {/* Results area */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
          borderRadius: '12px',
          border: `1px solid ${dk.border}`,
          backgroundColor: dk.bg,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          mx: 1,
        }}
      >
        {/* Scrollable results */}
        <Box
          data-results-scroll
          data-tour="search-results-area"
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: dk.scrollThumb,
              borderRadius: 3,
            },
          }}
        >
          <SearchCitations />
        </Box>

        {/* Pagination */}
        <Box sx={{ p: 1, flexShrink: 0 }}>
          <SearchPagination
            containerStyle={{
              padding: '8px 16px',
              backgroundColor: dk.elevated,
              borderRadius: '8px',
              border: `1px solid ${dk.borderLight}`,
            }}
          />
        </Box>
      </Box>
    </Box>
  )

  const documentPanel = (
    <Box
      data-tour="search-doc-viewer"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          padding: '8px',
        }}
      >
        <DocumentViewer
          document={searchDocument}
          isLoadingDocument={isSearchLoadingDocument}
          documentSourceUrl={searchDocumentSourceUrl}
          selectedCitation={searchSelectedCitation}
          setDocument={setSearchDocument}
        />
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden', backgroundColor: dk.surface }}>
      {/* Left: Filter Sidebar */}
      {!isMobile() && (
        <SearchFilterSidebar mode={mode} />
      )}

      {/* Center + Right: Resizable panels */}
      {!isMobile() ? (
        <Box sx={{ flex: 1, display: 'flex', height: '100%', minWidth: 0, overflow: 'hidden' }}>
          <Group direction="horizontal" style={{ width: '100%', height: '100%' }}>
            <Panel defaultSize={50} minSize={25}>
              {resultsPanel}
            </Panel>

            <Separator
              style={{
                width: '4px',
                backgroundColor: theme.palette.divider,
                cursor: 'col-resize',
                transition: prefersReducedMotion
                  ? 'none'
                  : 'background-color 0.2s ease, opacity 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.palette.primary.main
                e.currentTarget.style.opacity = '0.7'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = theme.palette.divider
                e.currentTarget.style.opacity = '1'
              }}
            />

            <Panel defaultSize={50} minSize={25}>
              {documentPanel}
            </Panel>
          </Group>
        </Box>
      ) : (
        resultsPanel
      )}
    </Box>
  )
}

export default SearchResultsView
